import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomCheckbox from '../../../shared-components/custom-check-box/CustomCheckBox';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
// import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { stringValidation } from '../../../shared-functions/Validation';
import PenaltyManagementApi from '../../../utils/api/penalty-management';

interface UserAddInterface {
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveTo: string;
  status: boolean;
  dynamicPricing: PricingInterface;
  durationAmountList: DurationAmountListInterface[];
}

interface PricingInterface {
  applyAll: boolean;
  base: boolean;
  peak: boolean;
  offpeak: boolean;
}
interface DurationAmountListInterface {
  from: string;
  to: string;
  mode: string;
  amount: string;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * get Dynamic Pricing Array
 * @param pricing
 * @returns
 */
const getDynamicPricingArray = (pricing: PricingInterface): string[] => {
  const result: string[] = [];

  if (pricing.base) result.push('base');
  if (pricing.peak) result.push('peak');
  if (pricing.offpeak) result.push('offpeak');

  return result;
};

/**
 * Checks whether the "From (Mins)" value is invalid
 * @param rule - Rule object containing time range values
 * @returns true if "from" is empty or negative
 */
const isFromInvalid = (rule: DurationAmountListInterface): boolean =>
  rule.from === '' || Number(rule.from) < 0;

/**
 * Checks whether the "To (Mins)" value is invalid
 * @param rule - Rule object containing time range values
 * @returns true if "to" is empty or less than "from"
 */
const isToInvalid = (rule: DurationAmountListInterface): boolean =>
  rule.to === '' || Number(rule.to) < Number(rule.from);

/**
 * Checks whether the "Mode" field is invalid
 * @param rule - Rule object containing mode information
 * @returns true if mode is not selected
 */
const isModeInvalid = (rule: DurationAmountListInterface): boolean => rule.mode === '';

/**
 * Checks whether the "Amount" field is invalid
 * @param rule - Rule object containing amount value
 * @returns true if amount is empty
 */
const isAmountInvalid = (rule: DurationAmountListInterface): boolean => rule.amount === '';

/**
 * Converting dynamic pricing array to object
 * @param array
 * @returns
 */
const dynamicPricingArrayToObject = (array: string[] = []) => ({
  applyAll: array.length === 3,
  base: array.includes('base'),
  peak: array.includes('peak'),
  offpeak: array.includes('offpeak'),
});

/**
 * Global Setting Component
 */
function GlobalSetting({
  closeModal,
  functionCall,
}: {
  closeModal: (value: string) => void;
  functionCall: () => void;
}) {
  const { t, i18n } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const [buttonStatus, setButtonStatus] = useState(false);
  const [ruleErrors, setRuleErrors] = useState<{
    [index: number]: { from: boolean; to: boolean };
  }>({});
  const [errorMessage, setErrorMessage] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  const [penaltyDetails, setPenaltyDetails] = useState<UserAddInterface>({
    ceilingType: '',
    ceilingValue: 0,
    triggerMessage: [],
    effectiveFrom: '',
    effectiveTo: '',
    status: false,
    dynamicPricing: { applyAll: false, base: false, peak: false, offpeak: false },
    durationAmountList: [{ from: '', to: '', mode: '', amount: '' }],
  });

  /**
   * Normal Input Handler
   */
  const handleInputChange = (name: string, value: string | number | boolean | string[]) => {
    setPenaltyDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle Pricing Checkbox Changes
   */
  const handlePricingChange = (name: keyof PricingInterface, value: boolean) => {
    setPenaltyDetails((previous) => {
      const updatedPricing = { ...previous.dynamicPricing, [name]: value };

      // Auto activate applyAll when all 3 are true
      updatedPricing.applyAll =
        updatedPricing.base && updatedPricing.peak && updatedPricing.offpeak;
      return {
        ...previous,
        dynamicPricing: updatedPricing,
      };
    });
  };

  /**
   * Apply All Switch
   */
  const handleApplyAllChange = (checked: boolean) => {
    setPenaltyDetails((previous: any) => ({
      ...previous,
      dynamicPricing: {
        applyAll: checked,
        base: checked,
        peak: checked,
        offpeak: checked,
      },
    }));
  };

  /**
   * Handling penalty feature
   * @param checked
   */
  const handlePenaltyFeature = (checked: boolean) => {
    setPenaltyDetails((previous: UserAddInterface) => ({
      ...previous,
      status: checked,
    }));
  };

  const allModes = [
    { label: t('grace'), value: 'grace' },
    { label: t('flatRate'), value: 'flatRate' },
    { label: t('perMinute'), value: 'perMinute' },
  ];

  /**
   * Add rule row
   */
  const addRule = () => {
    const index = penaltyDetails.durationAmountList.length - 1;
    const lastRule = penaltyDetails.durationAmountList[index];

    const from = Number(lastRule.from);
    const to = Number(lastRule.to);

    let fromError = false;
    let toError = false;

    if (lastRule.from === '') fromError = true;
    if (lastRule.to === '') toError = true;
    if (lastRule.from !== '' && lastRule.to !== '' && to < from) {
      fromError = true;
      toError = true;
    }
    if (lastRule.to === '' || to <= from) {
      toError = true;
    }
    if (fromError || toError) {
      setRuleErrors((previous) => ({
        ...previous,
        [index]: { from: fromError, to: toError },
      }));
      return;
    }

    // ✅ CORRECT STATE UPDATE
    setPenaltyDetails((previous) => ({
      ...previous,
      durationAmountList: [
        ...previous.durationAmountList,
        {
          from: String(to),
          to: '',
          mode: '',
          amount: '',
        },
      ],
    }));
  };

  /**
   * Delete rule row
   */
  const deleteRule = (index: number) => {
    setPenaltyDetails((previous) => ({
      ...previous,
      durationAmountList: previous.durationAmountList.filter((_, input) => input !== index),
    }));
  };

  /**
   * Update rule row field value
   */
  const updateRule = (index: number, name: keyof DurationAmountListInterface, value: string) => {
    const durationAmount = [...penaltyDetails.durationAmountList];

    // Update field
    durationAmount[index][name] = value;

    // ✅ If mode is GRACE → force amount = "0"
    if (name === 'mode' && value === 'grace') {
      durationAmount[index].amount = '0';
    }

    // ✅ If switching away from grace → clear amount
    if (name === 'mode' && value !== 'grace') {
      durationAmount[index].amount = '';
    }

    setPenaltyDetails((previous) => ({
      ...previous,
      durationAmount,
    }));
  };

  /**
   * Filter available mode options based on already selected modes
   */
  const getAvailableModes = (currentIndex: number) => {
    const selectedModes = penaltyDetails.durationAmountList.map((r) => r.mode).filter(Boolean);

    return allModes.filter((m) => {
      // Allow any mode if it's already selected for the current index (editing case)
      if (penaltyDetails.durationAmountList[currentIndex].mode === m.value) return true;
      // Make "Grace" unique: if already selected anywhere else, hide it
      if (m.value === 'grace' && selectedModes.includes('grace')) {
        return false;
      }

      // Allow Flat Rate & Per Minute multiple times
      return true;
    });
  };

  /**
   * Rules Valid
   */
  const isRulesValid = (): boolean => {
    let valid = true;
    const errors: {
      [index: number]: { from: boolean; to: boolean };
    } = {};

    penaltyDetails.durationAmountList.forEach((rule, index) => {
      const from = Number(rule.from);
      const to = Number(rule.to);

      let fromError = false;
      let toError = false;

      // 1️ Empty checks
      if (rule.from === '') fromError = true;
      if (rule.to === '') toError = true;

      // 2️ Same value (10–10)
      if (rule.from !== '' && rule.to !== '' && from === to) {
        toError = true;
      }

      // 3️ to < from
      if (rule.from !== '' && rule.to !== '' && to < from) {
        fromError = true;
        toError = true;
      }

      if (index > 0) {
        const previousTo = Number(penaltyDetails.durationAmountList[index - 1].to);

        if (rule.from !== '' && !Number.isNaN(previousTo) && from !== previousTo) {
          fromError = true; // ❌ breaks chain
        }
      }

      if (fromError || toError) {
        valid = false;
        errors[index] = { from: fromError, to: toError };
      }

      // 5️⃣ Mode validation
      if (!rule.mode) valid = false;

      // 6️⃣ Amount validation
      if (rule.mode !== 'grace' && rule.amount === '') valid = false;
    });

    setRuleErrors(errors);
    return valid;
  };

  const pricingSelected =
    penaltyDetails.dynamicPricing.base ||
    penaltyDetails.dynamicPricing.peak ||
    penaltyDetails.dynamicPricing.offpeak;

  const isDateInvalid =
    penaltyDetails.effectiveFrom &&
    penaltyDetails.effectiveTo &&
    new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom)) >
      new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveTo));
  /**
   * Submit payload to API
   */
  const handleSubmit = async () => {
    setButtonStatus(true);
    if (
      penaltyDetails.ceilingType === '' ||
      !stringValidation(1, 100, penaltyDetails.ceilingType) ||
      penaltyDetails.ceilingValue === 0 ||
      penaltyDetails.triggerMessage.length === 0 ||
      penaltyDetails.effectiveFrom === '' ||
      penaltyDetails.effectiveTo === '' ||
      new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom)) >
        new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveTo)) ||
      !pricingSelected ||
      !isRulesValid()
    ) {
      setErrorMessage(true);
      setButtonStatus(false);
      return;
    }
    try {
      const payload = {
        ceilingType: penaltyDetails.ceilingType,
        ceilingValue: penaltyDetails.ceilingValue,
        triggerMessage: penaltyDetails.triggerMessage,
        effectiveFrom: ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom),
        effectiveTo: ConvertDDMMYYYYtoISO(penaltyDetails.effectiveTo),
        dynamicPricingAll: penaltyDetails.dynamicPricing.applyAll,
        dynamicPricing: getDynamicPricingArray(penaltyDetails.dynamicPricing),
        durationAmountList: penaltyDetails.durationAmountList.map((previous) => ({
          amount: previous.amount,
          mode: previous.mode,
          from: previous.from,
          to: previous.to,
        })),
        status: penaltyDetails.status,
      };
      await PenaltyManagementApi.createGlobalPenalty(payload);
      functionCall();
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        rightCorner: '',
        notificationModal: true,
        content: (
          <SuccessNotification
            title={t('globalSettingsUpdated')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalBoxStatus('close');
              closeModal('');
            }}
          />
        ),
      });
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 404,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    } finally {
      setButtonStatus(false);
    }
  };

  /**
   * Go back
   */
  const handleCancel = () => {
    closeModal('');
  };

  /**
   * Fetching global settings data
   */
  const fetchGlobalSettingsData = async () => {
    try {
      const response = await PenaltyManagementApi.viewGlobalSettings();
      const data = response?.data;
      setPenaltyDetails((previous) => ({
        ...previous,
        ...data,
        dynamicPricing: dynamicPricingArrayToObject(data?.dynamicPricing || []),
        durationAmountList: data?.durationAmountList?.length
          ? data.durationAmountList.map((r: any) => ({
              from: String(r.from),
              to: String(r.to),
              mode: r.mode,
              amount: String(r.amount ?? ''),
            }))
          : [{ from: '', to: '', mode: '', amount: '' }],
      }));
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 404,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    fetchGlobalSettingsData();
  }, []);

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        // rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="global-setting-wrapper" dir={i18n.dir()}>
        <div>
          {' '}
          {/* <PageTitle title={t('accountSettings')} /> */}
        </div>

        {/* Penalty Feature */}
        <div className="switch-row">
          <SwitchButton checked={penaltyDetails.status} change={handlePenaltyFeature} />
          <span>{t('penaltyFeature')}</span>
        </div>

        <div className="section-box">
          <div className="Ceiling-box">
            {/* Ceiling Type */}
            <SelectFiled
              name="ceilingType"
              inputLabel={t('ceilingType')}
              selectedValue={penaltyDetails.ceilingType}
              placeholder={t('selectCeilingType')}
              onChangeValue={handleInputChange}
              valueRequired
              error={errorMessage && penaltyDetails.ceilingType === ''}
              errorMessage={t('ceilingTypeRequired')}
              options={[
                { label: t('fixedAmount'), value: 'fixedAmount' },
                { label: t('percentageOfBase'), value: 'percentage' },
              ]}
            />
            {/* Max Ceiling */}
            <CustomInput
              name="ceilingValue"
              inputLabel={t('maxCeilingValue')}
              type="number"
              valueRequired
              placeholder={t('enterValue')}
              value={penaltyDetails.ceilingValue}
              onChangeValue={handleInputChange}
              error={errorMessage && penaltyDetails.ceilingValue <= 0}
              errorMessage={t('maxCeilingRequired')}
              disallowNegative
            />
          </div>
          {/* Trigger Message */}
          <MultiSelectDropdown
            name="triggerMessage"
            title={t('triggerMessage')}
            selectedValues={penaltyDetails.triggerMessage}
            onSelectionChange={handleInputChange}
            placeholder={t('selectOption')}
            placeholderFilled={t('options')}
            options={[
              { label: t('suspendedEvse'), value: 'SuspendedEVSE' },
              { label: t('suspendedEv'), value: 'SuspendedEV' },
              { label: t('faulted'), value: 'Faulted' },
              { label: t('finishing'), value: 'Finishing' },
            ]}
            search={false}
            multiSelectDisable={false}
            valueRequired
            fontSize="small"
            selectedChips
            error={errorMessage && penaltyDetails.triggerMessage.length === 0}
            errorMessage={t('triggerMessageRequired')}
          />
          {/* Date Range */}
          <div className="date-box-content">
            <CustomDatePicker
              inputLabel={t('startDate')}
              value={ConvertUTCtoDate(penaltyDetails.effectiveFrom)}
              name="effectiveFrom"
              valueRequired
              onChangeValue={handleInputChange}
              placeholder={t('dateFormat')}
              error={Boolean(
                errorMessage && (penaltyDetails.effectiveFrom === '' || isDateInvalid),
              )}
              errorMessage={
                penaltyDetails.effectiveFrom === ''
                  ? t('startDateRequired')
                  : t('startDateGreaterError') // add this translation
              }
              disablePastDates
            />
            <CustomDatePicker
              inputLabel={t('endDate')}
              value={ConvertUTCtoDate(penaltyDetails.effectiveTo)}
              name="effectiveTo"
              onChangeValue={handleInputChange}
              valueRequired
              placeholder={t('dateFormat')}
              error={Boolean(errorMessage && (penaltyDetails.effectiveTo === '' || isDateInvalid))}
              errorMessage={
                penaltyDetails.effectiveTo === '' ? t('endDateRequired') : t('endDateLessError')
              }
              disablePastDates
            />
          </div>
        </div>
        {/* Apply All */}
        <div className="apply-pricing-content">
          <div className="swith-content">
            <div className="switch-row">
              <SwitchButton
                checked={penaltyDetails.dynamicPricing.applyAll}
                change={handleApplyAllChange}
              />
              <span>{t('applyToAll')}</span>
            </div>

            {/* Dynamic Pricing */}
            <p className="sub-title">{t('dynamicPricingTitle')}</p>
            {/* Show only when applyAll=false */}
            {!penaltyDetails.dynamicPricing.applyAll && (
              <div className="checkbox-row">
                <div className="checkbox">
                  <CustomCheckbox
                    checkboxLabel={t('base')}
                    name="base"
                    value={penaltyDetails?.dynamicPricing?.base}
                    onChangeValue={(_, value) => handlePricingChange('base', value as boolean)}
                    error={errorMessage && !pricingSelected}
                    errorMessage={t('baseRequired')}
                  />
                </div>
                <div className="checkbox">
                  <CustomCheckbox
                    checkboxLabel={t('peak')}
                    name="peak"
                    value={penaltyDetails?.dynamicPricing?.peak}
                    onChangeValue={(_, value) => handlePricingChange('peak', value as boolean)}
                    error={errorMessage && !pricingSelected}
                    errorMessage={t('peakRequired')}
                  />
                </div>
                <div className="checkbox">
                  <CustomCheckbox
                    checkboxLabel={t('offPeak')}
                    name="offpeak"
                    value={penaltyDetails?.dynamicPricing?.offpeak}
                    onChangeValue={(_, value) => handlePricingChange('offpeak', value as boolean)}
                    error={errorMessage && !pricingSelected}
                    errorMessage={t('offPeakRequired')}
                  />
                </div>
              </div>
            )}
          </div>

          {/* RULE SECTION */}
          <div className="rule-box-content">
            {penaltyDetails.durationAmountList.map((rule, index) => (
              <>
                <div className="rule-box" key={rule.from}>
                  {/* FROM */}
                  <CustomInput
                    name="from"
                    inputLabel={t('fromMins')}
                    placeholder="0"
                    type="number"
                    value={rule.from}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    error={ruleErrors[index]?.from || (errorMessage && isFromInvalid(rule))}
                    errorMessage={t('fromRequired')}
                    disallowNegative
                  />
                  {/* TO */}
                  <CustomInput
                    name="to"
                    inputLabel={t('toMins')}
                    placeholder="0"
                    type="number"
                    value={rule.to}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    error={ruleErrors[index]?.to || (errorMessage && isToInvalid(rule))}
                    errorMessage={rule.to === '' ? t('toRequired') : t('invalidRange')}
                    disallowNegative
                  />
                  {/* MODE */}
                  <SelectFiled
                    name="mode"
                    inputLabel={t('mode')}
                    placeholder={t('selectMode')}
                    selectedValue={rule.mode}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    options={getAvailableModes(index)}
                    error={errorMessage && isModeInvalid(rule)}
                    errorMessage={t('modeRequired')}
                  />
                  <CustomInput
                    name="amount"
                    inputLabel={t('amount')}
                    placeholder={t('enterValue')}
                    type="number"
                    value={rule.amount}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    disabled={rule.mode === 'grace'}
                    error={errorMessage && isAmountInvalid(rule)}
                    errorMessage={t('amountRequired')}
                    disallowNegative
                  />

                  {/* Delete Button */}
                  <div className="delete-button">
                    {penaltyDetails.durationAmountList.length > 1 && (
                      <MyButton
                        label=""
                        svgIcon={<DeleteIcon />}
                        buttonType="error"
                        onClickFunc={() => deleteRule(index)}
                        disableButton={buttonStatus}
                      />
                    )}
                  </div>
                </div>

                {/* Add Button ONLY for last row */}
                <div className="add-button">
                  {index === penaltyDetails.durationAmountList.length - 1 && (
                    <MyButton
                      label={t('add')}
                      svgIcon={<PlusIcon />}
                      buttonType="primary-outline-none"
                      onClickFunc={addRule}
                      disableButton={buttonStatus}
                    />
                  )}
                </div>
              </>
            ))}
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="button-row">
          <MyButton
            label={t('cancel')}
            buttonType="outline"
            onClickFunc={handleCancel}
            disableButton={buttonStatus}
          />
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmit}
            loading={buttonStatus}
          />
        </div>
      </div>
    </>
  );
}

export default GlobalSetting;
